import type { ReactNode } from 'react';
import { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ROW_ACTION_BUTTON } from '../../ConsolePanel';

/** The one line that goes in a customer's page, just before `</head>`. */
export const mainScriptTag = (websiteId: number) =>
  `<script async id="pertentoScript" src="https://scripts.pertento.ai/pertentoRuntimeExperiments.${websiteId}.js"></script>`;

type ScriptsModalProps = {
  websiteId: number;
  /** Draws the control that opens the dialog. Defaults to the websites list's row button. */
  renderTrigger?: (open: () => void) => ReactNode;
  /** A product tour anchor for the default trigger. */
  tourAnchor?: string;
};

export const ScriptsModal = (props: ScriptsModalProps) => {
  const { websiteId, renderTrigger, tourAnchor } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const scriptTag = mainScriptTag(websiteId);
  const opacityScript = `<style id="pertento-style-opacity">body { opacity: 0; }</style>
<script>setTimeout(() => { document.getElementById('pertento-style-opacity')?.remove() }, 500);</script>`;

  const copyToClipboard = (text: string) => async () => {
    await navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 3000);
  };

  // A span when the trigger is custom, so it can sit inside a sentence; the dialog itself portals out.
  const Wrapper = renderTrigger ? 'span' : 'div';

  return (
    <Wrapper>
      {renderTrigger ? (
        renderTrigger(() => setIsOpen(true))
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(true)}
          className={ROW_ACTION_BUTTON}
          data-tour={tourAnchor}
        >
          Implementation
        </Button>
      )}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Scripts</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Snippet
              title="Main Integration"
              description="This main script enables your website to function seamlessly with Pertento's services. For optimal performance, include this script just before the closing </head> tag. This implementation is essential to access Pertento's features."
              snippet={scriptTag}
              isCopied={copiedText === scriptTag}
              onCopy={copyToClipboard(scriptTag)}
            />
            <Snippet
              title="Anti-Flicker Snippet"
              description="To reduce page flickering and prevent visible loading of changes, add this optional script as high as possible in the <head> section. This adjustment grants Pertento greater control over the page for a smoother user experience."
              snippet={opacityScript}
              isCopied={copiedText === opacityScript}
              onCopy={copyToClipboard(opacityScript)}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setIsOpen(false)} className="h-9 rounded-lg">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Wrapper>
  );
};

type SnippetProps = {
  title: string;
  /** Shown beside the title, e.g. a link to a guide. */
  titleAside?: ReactNode;
  description: string;
  snippet: string;
  isCopied: boolean;
  onCopy: () => void;
};

/** Extracted so the two blocks cannot drift, and so neither one repeats the token set. */
export const Snippet = ({ title, titleAside, description, snippet, isCopied, onCopy }: SnippetProps) => (
  <div className="bg-panel2 border-line rounded-card grid gap-2 border p-3.5">
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
        <h6 className="text-foreground m-0 text-body font-medium">{title}</h6>
        {titleAside}
      </div>
      {isCopied ? (
        <span className="micro-label text-ok">copied</span>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={onCopy}
          className="micro-label text-dim hover:text-foreground hover:bg-hover h-7 rounded-lg px-2"
        >
          copy
        </Button>
      )}
    </div>
    <p className="supporting m-0">{description}</p>
    <pre className="bg-field border-line rounded-lg m-0 overflow-x-auto border p-3">
      <code className="log-line text-foreground break-all whitespace-pre-wrap">{snippet}</code>
    </pre>
  </div>
);
